import {
  createContext,
  useContext
} from "react";
import { useSessionStorage } from "react-use";

import { useMsal } from '@azure/msal-react';
import {
  systemuser as IXrmTypes_systemuser
} from "model/CRM";
import { ISecurityRole, ITeam } from "model/CRM.type";
import { ERoles } from 'model/Role.type';
import { ETeams } from 'model/Team.type';
import { appInsights } from "./Application/AppInsight";
import { Constants } from "./Application/Constants";

export interface IPrincipal {
  who?: any;
  systemUser?: IXrmTypes_systemuser;
  roles: ISecurityRole[];
  teams: ITeam[];
  username?: string;
  displayName?: string;
}

export type TRoles = {
  [key in ERoles]?: ISecurityRole;
}

export type TTeams = {
  [key in ETeams]?: ITeam;
}

export interface IAuthorization {
  teams: any;
  roles: TRoles;
  canView: boolean;
  canEdit: boolean;
  canUnderwritingReviewComplete: boolean;
}

export type TAuthenticationContext = {
  isAuthenticated: boolean;
  authorizations: IAuthorization | null;
  principal: IPrincipal | null;
  login: (principal: IPrincipal) => void;
  logout: (where?: "app+msal" | "app") => void;
};

const defaultValues: TAuthenticationContext = {
  isAuthenticated: false,
  authorizations: null,
  principal: null,
  login: (principal: IPrincipal) => { },
  logout: (where?: "app+msal" | "app") => { },
};

export const AuthenticationContext = createContext(defaultValues);

export const AuthenticationProvider = ({ children }: { children: JSX.Element }) => {
  const [authentication, setAuthentication] = useSessionStorage(Constants.app.authorizationStorageKey, defaultValues);
  const { instance } = useMsal();

  const login = (principal: IPrincipal) => {

    const roles = principal.roles.reduce((prev, curr) => {
      prev[curr.name] = curr;
      return prev;
    }, {} as TRoles);

    const teams = principal.teams.reduce((prev, curr) => {
      prev[curr.name] = curr;
      return prev;
    }, {} as TTeams);

    const authorizations: IAuthorization = {
      roles: roles,
      teams: teams,
      canView: !!roles['BCBS - Power User'] || !!roles["BCBS - Group Configuration App (View Only)"],
      canEdit: !!roles['BCBS - Power User'] || !!roles["BCBS - Group Configuration App (Edit)"],
      canUnderwritingReviewComplete: !!roles['BCBS - Power User'] || !!teams["Group Library Security - Underwriting"],
    }

    const _authentication = {
      ...authentication,
      isAuthenticated: true,
      principal: principal,
      authorizations: authorizations
    };

    //TODO add environment variable for this or remove from production build
    console.log(`[appl] AuthorizationProvider.login`, _authentication);

    if (principal?.systemUser?.systemuserid) {
      appInsights?.setAuthenticatedUserContext(principal.systemUser?.systemuserid, undefined, true);

      appInsights?.trackEvent({
        name: 'Authentication.login.identity',
        properties: {
          systemUser: principal.systemUser,
          app: Constants.app
        }
      });
    }


    setAuthentication(_authentication);
  }

  // 
  // if (authentication?.authorizations?.roles) {
  //   delete authentication.authorizations.roles['BCBS - Power User'];
  //   authentication.authorizations.roles['BCBS - Read Only Power User'] = {
  //     name: ERoles["BCBS - Read Only Power User"],
  //     roleid: '',
  //   };
  // }


  const logout = (where?: "app+msal" | "app") => {
    setAuthentication(defaultValues);

    if (where === "app+msal")
      instance.logout()
  };

  // Test code for authentications:
  // if (authentication?.authorizations?.roles)
  //   delete authentication.authorizations.roles["System Administrator"];
  // if (authentication?.authorizations?.roles)
  //   authentication.authorizations.roles['BCBS - PSA'] = { name: ERoles["BCBS - PSA"], roleid: '' }

  // after a context run, substitute defaults with right actions/values
  const authenticationValue: TAuthenticationContext = {
    authorizations: authentication.authorizations,
    isAuthenticated: authentication.isAuthenticated,
    principal: authentication.principal,
    login,
    logout
  };

  return (
    <AuthenticationContext.Provider value={authenticationValue}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export function useAuthentication() {
  return useContext(AuthenticationContext);
}
